import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MapPinIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  ShieldCheckIcon,
  PlusIcon,
  EyeIcon,
  CommandLineIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Alert } from '@/components/ui/Alert';
import { gpsApi, GPSDevice, VehicleTracking, AlarmRecord } from '@/api/gpsApi';
import { getVehicles, updateVehicle, Vehicle } from '@/api/fleetApi';
import toast from 'react-hot-toast';

interface VehicleWithGPS extends Vehicle {
  gpsDevice?: GPSDevice;
  tracking?: VehicleTracking;
  lastAlarm?: AlarmRecord;
  gpsDeviceId?: string; // Add GPS device IMEI field
}

const GPSFleetPage: React.FC = () => {
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleWithGPS | null>(null);
  const [showAddGPS, setShowAddGPS] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const queryClient = useQueryClient();

  // Fetch fleet vehicles from SmartTag School
  const { data: fleetData, isLoading: fleetLoading, error: fleetError } = useQuery({
    queryKey: ['fleet-vehicles'],
    queryFn: () => getVehicles()
  });

  // Fetch GPS devices from Protrack
  const { data: gpsData, isLoading: gpsLoading, error: gpsError } = useQuery({
    queryKey: ['gps-devices'],
    queryFn: () => gpsApi.getDeviceList(),
    refetchInterval: trackingEnabled ? 30000 : false // Refresh every 30 seconds when tracking
  });

  // Fetch real-time tracking for all vehicles
  const { data: trackingData, isLoading: trackingLoading, error: trackingError } = useQuery({
    queryKey: ['vehicle-tracking'],
    queryFn: () => gpsApi.getDeviceTracking(),
    refetchInterval: trackingEnabled ? 10000 : false, // Refresh every 10 seconds when tracking
    enabled: trackingEnabled
  });

  // Debug query results
  console.log('=== QUERY RESULTS ===');
  console.log('fleetLoading:', fleetLoading, 'fleetError:', fleetError);
  console.log('gpsLoading:', gpsLoading, 'gpsError:', gpsError);
  console.log('trackingLoading:', trackingLoading, 'trackingError:', trackingError);

  // Fetch recent alarms for all devices
  const { data: alarmsData } = useQuery({
    queryKey: ['recent-alarms'],
    queryFn: async () => {
      const gpsDevices = gpsData?.data?.data?.devices || [];
      if (gpsDevices.length === 0) return [];

      const endtime = Math.floor(Date.now() / 1000);
      const begintime = endtime - (24 * 60 * 60); // Last 24 hours

      const alarmPromises = gpsDevices.map((device: GPSDevice) =>
        gpsApi.getDeviceAlarms(device.imei, begintime, endtime)
          .then(response => ({
            imei: device.imei,
            alarms: response.data.alarmRecords
          }))
          .catch(() => ({ imei: device.imei, alarms: [] }))
      );

      return Promise.all(alarmPromises);
    },
    enabled: !!gpsData?.data?.data?.devices && gpsData.data.data.devices.length > 0
  });

  // Link GPS devices to SmartTag vehicles
  const vehiclesWithGPS: VehicleWithGPS[] = React.useMemo(() => {
    console.log('=== GPS FLEET DEBUG ===');
    console.log('fleetData:', fleetData);
    console.log('gpsData:', gpsData);
    console.log('trackingData:', trackingData);
    console.log('alarmsData:', alarmsData);
    
    const vehicles = fleetData?.data?.vehicles || [];
    const gpsDevices = gpsData?.data?.data?.devices || [];
    const trackingDevices = trackingData?.data?.data?.devices || [];
    
    console.log('Extracted vehicles:', vehicles);
    console.log('Extracted gpsDevices:', gpsDevices);
    console.log('Extracted trackingDevices:', trackingDevices);
    
    if (vehicles.length === 0) {
      console.log('No vehicles found, returning empty array');
      return [];
    }

    return vehicles.map((vehicle: Vehicle, index: number) => {
      console.log(`Processing vehicle ${index}:`, vehicle);
      
      // Get GPS device IMEI from engineNumber field (stored as GPS:IMEI)
      const gpsDeviceId = vehicle.engineNumber?.startsWith('GPS:')
        ? vehicle.engineNumber.replace('GPS:', '')
        : undefined;
      
      console.log(`Vehicle ${vehicle.vehicleNumber} - GPS ID:`, gpsDeviceId);

      const gpsDevice = gpsDeviceId ? gpsDevices.find(
        (device: GPSDevice) => device.imei === gpsDeviceId
      ) : undefined;
      
      console.log(`Found GPS device for ${vehicle.vehicleNumber}:`, gpsDevice);

      const tracking = gpsDeviceId ? trackingDevices.find(
        (track: VehicleTracking) => track.imei === gpsDeviceId
      ) : undefined;
      
      console.log(`Found tracking for ${vehicle.vehicleNumber}:`, tracking);

      const alarmEntry = gpsDeviceId ? alarmsData?.find(
        alarm => alarm.imei === gpsDeviceId
      ) : undefined;
      
      console.log(`Found alarm entry for ${vehicle.vehicleNumber}:`, alarmEntry);
      console.log(`Alarms array:`, alarmEntry?.alarms);
      
      const lastAlarm = alarmEntry?.alarms?.[0] || undefined;
      console.log(`Last alarm for ${vehicle.vehicleNumber}:`, lastAlarm);

      return {
        ...vehicle,
        gpsDeviceId,
        gpsDevice,
        tracking,
        lastAlarm
      } as VehicleWithGPS;
    });
  }, [fleetData, gpsData, trackingData, alarmsData]);

  // Link GPS device to vehicle
  const linkGPSMutation = useMutation({
    mutationFn: ({ vehicleId, imei }: { vehicleId: string; imei: string }) =>
      updateVehicle(vehicleId, {
        engineNumber: `GPS:${imei}`,
        isTracking: true,
        features: { gps: true }
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      toast.success('GPS device linked to vehicle successfully');
      setShowAddGPS(false);
    },
    onError: () => {
      toast.error('Failed to link GPS device');
    }
  });

  // Send device command
  const commandMutation = useMutation({
    mutationFn: ({ imei, command }: { imei: string; command: string }) =>
      gpsApi.sendCommand(imei, command),
    onSuccess: (response) => {
      toast.success(`Command sent: ${response.data.command}`);
      queryClient.invalidateQueries({ queryKey: ['vehicle-tracking'] });
    },
    onError: () => {
      toast.error('Failed to send command');
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OK': return 'text-green-600';
      case 'OFFLINE': return 'text-red-600';
      case 'BLOCKED': return 'text-red-600';
      case 'EXPIRED': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getAlarmSeverity = (alarmType: number) => {
    if ([1].includes(alarmType)) return 'CRITICAL';
    if ([26, 27, 28, 29].includes(alarmType)) return 'HIGH';
    if ([4, 5, 6].includes(alarmType)) return 'MEDIUM';
    return 'LOW';
  };

  if (fleetLoading || gpsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (fleetError || gpsError) {
    return (
      <div className="p-8">
        <Alert
          
          message={`Failed to load data: ${fleetError || gpsError}`}
        />
      </div>
    );
  }

  // Final debug before render
  console.log('=== RENDER DEBUG ===');
  console.log('vehiclesWithGPS:', vehiclesWithGPS);
  console.log('vehiclesWithGPS.length:', vehiclesWithGPS.length);

  return (
    <div className=" bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <TruckIcon className="w-8 h-8 text-blue-600 mr-3" />
                GPS Fleet Management
              </h1>
              <p className="text-gray-600 mt-2">
                Real-time vehicle tracking and fleet monitoring powered by Protrack GPS
              </p>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${gpsData ? 'bg-green-400' : 'bg-red-400'}`} />
                <span className="text-sm text-gray-600">
                  Protrack {gpsData ? 'Connected' : 'Disconnected'}
                </span>
              </div>

              <Button
                onClick={() => setTrackingEnabled(!trackingEnabled)}
                variant={trackingEnabled ? 'danger' : 'primary'}
                size="sm"
              >
                <BoltIcon className="w-4 h-4 mr-2" />
                {trackingEnabled ? 'Stop Live Tracking' : 'Start Live Tracking'}
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <TruckIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Vehicles</p>
                <p className="text-2xl font-bold text-gray-900">
                  {fleetData?.data.vehicles.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <MapPinIcon className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">GPS Devices</p>
                <p className="text-2xl font-bold text-gray-900">
                  {gpsData?.data.devices?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ShieldCheckIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Linked Vehicles</p>
                <p className="text-2xl font-bold text-gray-900">
                  {vehiclesWithGPS.filter(v => v.gpsDevice).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Recent Alarms</p>
                <p className="text-2xl font-bold text-gray-900">
                  {alarmsData?.reduce((total, device) => total + (device.alarms?.length || 0), 0) || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Fleet Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Fleet Overview</h2>
              <Button onClick={() => setShowAddGPS(true)} size="sm">
                <PlusIcon className="w-4 h-4 mr-2" />
                Link GPS Device
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GPS Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Alarm
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vehiclesWithGPS.map((vehicle: any) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <TruckIcon className="w-8 h-8 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {vehicle.vehicleNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {vehicle.make} {vehicle.vehicleModel} • {vehicle.capacity} seats
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.gpsDevice ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {vehicle.gpsDevice.deviceName}
                          </div>
                          <div className="text-sm text-gray-500">
                            IMEI: {vehicle.gpsDevice.imei}
                          </div>
                          <div className="text-xs text-gray-400">
                            {vehicle.gpsDevice.deviceType}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 italic">No GPS device</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.tracking ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {vehicle.tracking.location.latitude.toFixed(6)}, {vehicle.tracking.location.longitude.toFixed(6)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {vehicle.tracking.location.speed} km/h • Course {vehicle.tracking.location.course}°
                          </div>
                          <div className="text-xs text-gray-400">
                            {vehicle.tracking.timing.gpsTime ?
                              new Date(vehicle.tracking.timing.gpsTime).toLocaleString() :
                              'No GPS data'
                            }
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Location unavailable</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.tracking ? (
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            vehicle.tracking.status.dataStatus === 'OK'
                              ? 'bg-green-100 text-green-800'
                              : vehicle.tracking.status.dataStatus === 'OFFLINE'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {vehicle.tracking.status.dataStatus}
                          </span>
                          <div className="text-xs text-gray-500">
                            ACC: {vehicle.tracking.status.acc} •
                            Battery: {vehicle.tracking.status.battery || 'N/A'}%
                          </div>
                        </div>
                      ) : vehicle.gpsDevice ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          GPS Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          No GPS
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      {vehicle.lastAlarm ? (
                        <div>
                          <div className={`text-sm font-medium ${
                            getAlarmSeverity(vehicle.lastAlarm.alarmType) === 'CRITICAL' ? 'text-red-600' :
                            getAlarmSeverity(vehicle.lastAlarm.alarmType) === 'HIGH' ? 'text-orange-600' :
                            'text-yellow-600'
                          }`}>
                            {vehicle.lastAlarm.alarmDescription}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(vehicle.lastAlarm.timing.gpsTime).toLocaleString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No recent alarms</span>
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => setSelectedVehicle(vehicle)}
                          size="sm"
                          variant="secondary"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          View
                        </Button>

                        {vehicle.tracking && vehicle.gpsDevice && (
                          <Button
                            onClick={() => commandMutation.mutate({
                              imei: vehicle.gpsDevice!.imei,
                              command: vehicle.tracking!.status.acc === 'ON' ? 'RELAY,1' : 'RELAY,0'
                            })}
                            size="sm"
                            variant={vehicle.tracking.status.acc === 'ON' ? 'danger' : 'primary'}
                            disabled={commandMutation.isPending}
                          >
                            <CommandLineIcon className="w-4 h-4 mr-1" />
                            {vehicle.tracking.status.acc === 'ON' ? 'Stop Engine' : 'Start Engine'}
                          </Button>
                        )}

                        {/* GPS Tracking & Playback for GPS-ready vehicles */}
                        {vehicle.gpsDevice && (
                          <>
                            <Button
                              onClick={() => {
                                // Open GPS tracking dashboard for this specific vehicle
                                window.open(`/admin/gps-tracking?vehicle=${vehicle.gpsDeviceId}`, '_blank');
                              }}
                              size="sm"
                              variant="primary"
                            >
                              📍 Track
                            </Button>
                            
                            <Button
                              onClick={() => {
                                // Open GPS playback for this specific vehicle
                                // For now, we'll use a simple alert - you can integrate the playback modal here
                                alert(`GPS Playback for ${vehicle.vehicleNumber} (IMEI: ${vehicle.gpsDeviceId})`);
                              }}
                              size="sm"
                              variant="ghost"
                            >
                              🕒 Playback
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Unlinked GPS Devices */}
        {gpsData?.data.devices && gpsData.data.devices.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Available GPS Devices</h3>
              <p className="text-sm text-gray-600">GPS devices not yet linked to vehicles</p>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gpsData.data.devices
                  .filter((device: GPSDevice) =>
                    !vehiclesWithGPS.some(v => v.gpsDeviceId === device.imei)
                  )
                  .map((device: GPSDevice) => {
                    const tracking = trackingData?.data.devices.find(
                      (track: VehicleTracking) => track.imei === device.imei
                    );

                    return (
                      <div key={device.imei} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-900">{device.deviceName}</h4>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            device.status.isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {device.status.isOnline ? 'Online' : 'Offline'}
                          </span>
                        </div>

                        <div className="space-y-2 text-sm text-gray-600">
                          <div>IMEI: <span className="font-mono">{device.imei}</span></div>
                          <div>Type: {device.deviceType}</div>
                          <div>Plate: {device.plateNumber || 'Not set'}</div>
                          {device.simCard && <div>SIM: {device.simCard}</div>}

                          {tracking && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="text-xs">
                                Location: {tracking.location.latitude.toFixed(4)}, {tracking.location.longitude.toFixed(4)}
                              </div>
                              <div className="text-xs">
                                Status: <span className={getStatusColor(tracking.status.dataStatus)}>
                                  {tracking.status.dataStatus}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        <Button
                          onClick={() => setShowAddGPS(true)}
                          size="sm"
                          className="w-full mt-4"
                          variant="secondary"
                        >
                          Link to Vehicle
                        </Button>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Real-time Alerts */}
        {alarmsData && alarmsData.some(d => d.alarms?.length > 0) && (
          <div className="mt-8 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mr-2" />
                Recent Alarms (Last 24 Hours)
              </h3>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {alarmsData
                  .filter(device => device.alarms.length > 0)
                  .map(device => (
                    <div key={device.imei} className="border-l-4 border-orange-400 pl-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        {gpsData.data.devices.find((d: GPSDevice) => d.imei === device.imei)?.deviceName || device.imei}
                      </h4>
                      <div className="space-y-2">
                        {device.alarms.slice(0, 3).map((alarm, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <div className="flex items-center">
                              <span className={`w-2 h-2 rounded-full mr-2 ${
                                getAlarmSeverity(alarm.alarmType) === 'CRITICAL' ? 'bg-red-500' :
                                getAlarmSeverity(alarm.alarmType) === 'HIGH' ? 'bg-orange-500' :
                                'bg-yellow-500'
                              }`} />
                              <span className="font-medium">{alarm.alarmDescription}</span>
                            </div>
                            <span className="text-gray-500">
                              {new Date(alarm.timing.gpsTime).toLocaleString()}
                            </span>
                          </div>
                        ))}
                        {device.alarms.length > 3 && (
                          <div className="text-sm text-gray-500">
                            +{device.alarms.length - 3} more alarms
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Loading States */}
        {(trackingLoading) && (
          <div className="mt-8">
            <Alert
              type="info"
              message="Updating live tracking data..."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GPSFleetPage;
